/**
 * Mochia App root.
 *
 * Composition order (per Section 4 / 4.7 / 14.2):
 *   1. read auth state from the auth slice
 *   2. unauthed → render <LoginScreen />
 *   3. authed   → pick WebShell vs MobileShell based on viewport, render the
 *      route's screen
 *
 * Section 8.3 ties in here:
 *   - On first authed mount, run the recurring catch-up scheduler.
 *   - Re-run on `visibilitychange` → `visible` so a user returning to the
 *     tab after some time gets caught up. The scheduler's own dedupe key
 *     prevents double-enqueue when visibility flaps in quick succession.
 *
 * Section 14.2:
 *   - HomeScreen reads `auth.token`, `books.activeBookId`, transactions, and
 *     categories from the global store via per-slice selectors.
 *   - `handleAuthenticated` stamps the auth slice + kicks off the sheets
 *     bootstrap. Bootstrap failures surface as a sign-out so the user can
 *     retry; we don't display a half-broken authed shell.
 *
 * Sharp edges:
 *   - We do NOT persist tokens — `auth.setSession` only updates memory; the
 *     persist middleware's `partialize` excludes the auth slice.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import LoginScreen from '@/components/auth/LoginScreen';
import MobileShell from '@/components/shell/MobileShell';
import WebShell from '@/components/shell/WebShell';
import SettingsScreen from '@/components/settings/SettingsScreen';
import HomeScreen from '@/components/home/HomeScreen';
import BooksSheet, { NewBookSheet, EditBookSheet } from '@/components/books/BooksSheet';
import AnalyticsScreen from '@/components/analytics/AnalyticsScreen';
import RecurringScreen from '@/components/recurring/RecurringScreen';
import NotifCenterScreen from '@/components/notifications/NotifCenterScreen';
import NotifSettingsScreen from '@/components/settings/NotifSettingsScreen';
import CloudSyncScreen from '@/components/settings/CloudSyncScreen';
import AddTxScreen from '@/components/transactions/AddTxScreen';
import ReceiptScanScreen from '@/components/ocr/ReceiptScanScreen';
import AllTxScreen from '@/components/transactions/AllTxScreen';
import SearchScreen from '@/components/transactions/SearchScreen';
import CategoriesScreen from '@/components/categories/CategoriesScreen';
import { applyReorder } from '@/components/categories/onReorder';
import Toast from '@/components/common/Toast';
import type { MobileTabKey } from '@/components/shell/TabBar';
import type { SidebarTabKey } from '@/components/shell/Sidebar';
import { useViewport } from '@/lib/viewport';
import { useRoute, type RoutePath } from '@/lib/router';
import { useStore } from '@/state/store';
import { bootstrapWithMeta } from '@/lib/sheets/bootstrap';
import { checkSchemaVersion, runMigrations, SchemaTooNewError } from '@/lib/sheets/migrate';
import { createSheetsClient, type SheetsClient } from '@/lib/sheets/client';
import { initSyncWiring, tearDownSyncWiring, enqueueOp } from '@/lib/sheets/syncWiring';
import { startRealtimePoll, type RealtimePollHandle } from '@/lib/sheets/realtimePoll';
import { booksRepo } from '@/lib/sheets/repositories/books';
import { categoriesRepo } from '@/lib/sheets/repositories/categories';
import { transactionsRepo } from '@/lib/sheets/repositories/transactions';
import { recurringRepo } from '@/lib/sheets/repositories/recurring';
import { notificationsRepo } from '@/lib/sheets/repositories/notifications';
import { metaRepo } from '@/lib/sheets/repositories/meta';
import { setToken, clear as clearSession, getToken as getSessionToken } from '@/lib/auth/session';
import { postRefresh, postLogout } from '@/lib/auth/serverRefresh';
import { getAccount, setAccount, clearAccount } from '@/lib/auth/account';
import { setCookieKnown, clearCookieKnown, hasCookieKnown } from '@/lib/auth/cookieKnown';
import AuthCallbackScreen from '@/components/auth/AuthCallbackScreen';
import {
  getLastActive,
  setLastActive,
  clearLastActive,
} from '@/lib/auth/lastActive';
import type { SilentRefreshFailureReason } from '@/lib/auth/silentRefreshErrors';
import { fetchGoogleUserinfo } from '@/lib/auth/userinfo';
import { runColdLoadAuth } from '@/components/auth/coldLoad';
import { isDevMode, DEV_FAKE_TOKEN, DEV_FAKE_EMAIL } from '@/lib/devmode';
import { createMemorySheetsClient } from '@/lib/devmode/memoryClient';
import {
  consumeJoinUrlIfPresent,
  getSharedSpreadsheetId,
  clearSharedSpreadsheetId,
} from '@/lib/sharing';
import { runScheduler, kickScheduler, __resetSchedulerLastRun } from '@/lib/recurring/runScheduler';
import { LastBookUndeletableError } from '@/state/slices/books';
import { InvalidTokenFormatError, hasHint } from '@/lib/errors';
import type { BookRow, TransactionRow, CategoryRow } from '@/lib/sheets/types';
import { buildPercento } from '@/lib/analytics/percento';
import PercentoBar from '@/components/analytics/PercentoBar';
import { formatYmd, startOfMonth, endOfMonth } from '@/lib/format/date';
import { genId as repoGenId } from '@/lib/sheets/repositories/_base';
import { SEED_CATEGORIES, SEED_SUBCATEGORIES } from '@/lib/sheets/bootstrap';
import { parseTags } from '@/lib/tags';
import { buildRecentNotesIndex } from '@/lib/recentNotes';

const SIDEBAR_TITLES: Record<SidebarTabKey, string> = {
  home: '首頁',
  'all-tx': '全部紀錄',
  analytics: '分析',
  cats: '分類',
  recur: '固定週期',
  search: '搜尋',
  notif: '通知',
};

const ROUTE_TO_SIDEBAR: Partial<Record<RoutePath, SidebarTabKey>> = {
  '/home': 'home',
  '/all-tx': 'all-tx',
  '/analytics': 'analytics',
  '/cats': 'cats',
  '/recur': 'recur',
  '/search': 'search',
  '/notif': 'notif',
};


// Module-level cache for the bootstrapped Sheets client.
// Spreadsheet id lives in `activeSpreadsheet.ts` so non-shell modules can
// read it without importing App (avoiding a circular dep).
let _client: SheetsClient | null = null;
let _realtimePoll: RealtimePollHandle | null = null;

/**
 * Fire-and-forget Google userinfo fetch — used by every auth path
 * (fresh login, silent refresh, cold mount with persisted token) to
 * populate the SettingsScreen user-card hero. Errors are logged and
 * swallowed; the UI gracefully falls back to the email's local-part.
 */
function refreshUserProfile(token: string): void {
  void fetchGoogleUserinfo(token)
    .then((info) => {
      useStore.getState().auth.setProfile({
        name: info.name,
        picture: info.picture,
        email: info.email ?? null,
      });
      if (info.email) setAccount(info.email);
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[Mochia/auth] userinfo fetch failed', err);
    });
}

/**
 * In-flight bootstrap Promise. React StrictMode (dev) mounts effects
 * twice in quick succession; without this latch the second mount fires
 * a second `runBootstrapPipeline()` before the first has set the
 * activeSpreadsheetId guard, so two concurrent pipelines call
 * `loadInitial` → eight Sheets API reads instead of four → 429
 * "Too Many Requests" on the meta probe. Caching the promise lets the
 * second caller await the same result without doubling the network
 * load. Cleared in `finally` so logout + re-login still works.
 */
let _bootstrapInFlight: Promise<void> | null = null;

export { getActiveSpreadsheetId } from '@/lib/sheets/activeSpreadsheet';
import { setActiveSpreadsheetId } from '@/lib/sheets/activeSpreadsheet';
import { getActiveSpreadsheetId as readActiveId } from '@/lib/sheets/activeSpreadsheet';

function getClient(): SheetsClient {
  if (_client) return _client;
  if (isDevMode()) {
    _client = createMemorySheetsClient();
    return _client;
  }
  _client = createSheetsClient({
    // Single source of truth for the bearer token: session.ts holds it.
    getAccessToken: getSessionToken,
    // 401 path: gis.ts fetchWithAuth now calls postRefresh internally.
    // The silentRefresh dep here is no longer needed — pass a no-op to
    // satisfy the interface until createSheetsClient is refactored.
    silentRefresh: async () => {
      // No-op: fetchWithAuth in gis.ts handles the 401→postRefresh retry.
    },
  });
  return _client;
}

export default function App(): JSX.Element {
  const isAuthed = useStore((s) => !!s.auth.token);
  const viewport = useViewport();
  const { path, navigate } = useRoute();

  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  // `extend-session-lifetime`: the cold-load silent-refresh failure reason is
  // surfaced to LoginScreen so the user gets actionable copy (Decision 4).
  // `null` means no cold-load failure has happened yet (or it was cleared).
  const [coldLoadFailureReason, setColdLoadFailureReason] =
    useState<SilentRefreshFailureReason | null>(null);

  // Migration notice (Decision 8): detect legacy localStorage session keys
  // ONCE on mount, before session.ts module-init would have removed them.
  // session.ts module-init runs at import time (before App mounts), so we
  // check the flag here instead; a dedicated flag `mochia.migration.v1` is
  // set by session.ts only if it found and cleaned up v2 keys.
  // Simpler approach: session.ts already cleaned the keys at module load time,
  // so we track migration at the App level by pre-checking before the first
  // render using a useState initialiser (lazy initial state runs synchronously).
  const [migrationNotice] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return false;
    try {
      // If either legacy key exists at first render, the user is migrating.
      // session.ts module-init will remove them, but useState runs first.
      return (
        localStorage.getItem('mochia.session.v2') !== null ||
        localStorage.getItem('mochia.session.v1') !== null
      );
    } catch {
      return false;
    }
  });

  // Upstream error toast (UpstreamUnavailable during cold load refresh)
  const [upstreamErrorToast, setUpstreamErrorToast] = useState(false);

  // Consume `?join=<spreadsheetId>` ONCE on mount, before login. Without
  // this, the URL was only read inside runBootstrapPipeline (after login),
  // and a second user opening the join URL while logged-out had no effect
  // until they signed in. Persisting early lets sessionStorage rehydrate
  // paths and dev-mode also pick up the override naturally.
  useEffect(() => {
    consumeJoinUrlIfPresent();
  }, []);

  // Stuck-on-/login rescue: SettingsScreen sign-out navigates to /login,
  // but the app's only "/login" route is a placeholder ("本畫面將在後續任務
  // 中實作。"). When the user signs back in via the cold-load silent path
  // OR an in-place rehydrate, isAuthed flips true but URL stays on /login
  // — no path is rewired to navigate elsewhere on those paths. Snap them
  // back to /home unconditionally; the LoginScreen overlay sits on top of
  // /home content while !isAuthed, so this doesn't disturb the sign-in UI.
  useEffect(() => {
    if (isAuthed && path === '/login') {
      navigate('/home');
    }
  }, [isAuthed, path, navigate]);

  const handleLogout = useCallback(async () => {
    useStore.getState().auth.clearSession();
    // Fire-and-forget: server revokes the Google grant and clears the
    // mochia_rt cookie. Local cleanup proceeds regardless of server result
    // (postLogout never throws — see serverRefresh.ts).
    void postLogout();
    clearSession();
    setActiveSpreadsheetId(null);
    _client = null;
    if (_realtimePoll) {
      _realtimePoll.stop();
      _realtimePoll = null;
    }
    __resetSchedulerLastRun();
    tearDownSyncWiring();
    // Clear the join override so the next account isn't bound to this one's
    // shared workbook. Owner's own bootstrap will re-find their own file.
    clearSharedSpreadsheetId();
    // Clear the persisted login_hint so the next cold load goes straight to
    // LoginScreen rather than silently refreshing this (now-revoked) account.
    clearAccount();
    // Clear the lastActive timestamp so the 7-day idle gate is reset on
    // the next sign-in. Without this, a logout-then-relogin within 7 days
    // would inherit the previous user's idle window — harmless today
    // because the gate only fires on missing/old tokens, but cleaner.
    clearLastActive();
    // Clear the cookieKnown flag so the next cold load does not attempt
    // /api/auth/refresh (the cookie was cleared server-side by postLogout).
    clearCookieKnown();
  }, []);

  // Bootstrap pipeline — extracted from handleAuthenticated so the
  // rehydrate-on-refresh path can run it without re-stamping the token.
  const runBootstrapPipeline = useCallback(async (): Promise<void> => {
    // Coalesce concurrent calls (StrictMode double-mount, racing GIS
    // callbacks): if a pipeline is already running, return its promise
    // so the caller awaits the same bootstrap rather than firing a
    // parallel one and tripping Sheets API rate limits.
    if (_bootstrapInFlight) return _bootstrapInFlight;
    _bootstrapInFlight = (async (): Promise<void> => {
    setBootstrapping(true);
    setBootstrapError(null);
    try {
      const client = getClient();
      const sharedId = getSharedSpreadsheetId();
      const { spreadsheetId: id, preloaded } = sharedId
        ? await bootstrapWithMeta(client, { sharedSpreadsheetId: sharedId })
        : await bootstrapWithMeta(client);
      setActiveSpreadsheetId(id);
      // Refuse to operate when the on-disk schema is NEWER than this
      // client supports. Without this gate, an old build would silently
      // mis-read a sheet that a newer client already migrated (positions
      // shifted in v5: `tags` moved up, `time` folded into `date`) and
      // could re-corrupt rows. `runMigrations` below upgrades older
      // sheets but never downgrades — so this only fires when the user
      // installed an older app build (e.g. cached PWA shell on iOS).
      //
      // `preloaded?.meta` reuses the categories/books/meta snapshot
      // `bootstrapWithMeta` already read in one `valuesBatchGet` call
      // (data-layer spec requirement "Bootstrap Read Deduplication"); it's
      // `null` on first-time creation or when verifyAndRepair just topped
      // up `meta`, in which case both calls fall back to fetching fresh.
      const { schemaVersion } = await checkSchemaVersion(client, id, preloaded?.meta);
      await runMigrations(client, id, schemaVersion);
      initSyncWiring({ client, spreadsheetId: id });
      await loadInitial(client, id);
      // Recurring catch-up must run AFTER the recurring rows are in the
      // store. SchedulerEffect's mount pass races this pipeline (`isAuthed`
      // flips true before loadInitial resolves) and sees an empty store, so
      // kick a fresh pass now that data is loaded. The existing-tx dedup
      // overlay in runScheduler keeps a double pass idempotent.
      kickScheduler(useStore.getState(), enqueueOp);
      // Start polling for cross-user / cross-tab changes. Stops on logout
      // (handleLogout) and re-creates on next bootstrap. No-op in dev-mode
      // since the in-memory client has no other writers.
      if (_realtimePoll) {
        _realtimePoll.stop();
        _realtimePoll = null;
      }
      if (!isDevMode()) {
        _realtimePoll = startRealtimePoll({ client, spreadsheetId: id });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Mochia/auth] bootstrap failed', err);
      // Surface a friendly message for the most-likely sharing failure:
      // user followed a `?join=<id>` invite but doesn't have access to the
      // shared sheet yet (owner forgot step 1: Drive UI share).
      const usedSharedOverride = getSharedSpreadsheetId() !== null;
      const isSheetsForbiddenOrMissing =
        err instanceof Error &&
        'status' in err &&
        ((err as { status: number }).status === 403 ||
          (err as { status: number }).status === 404);
      if (usedSharedOverride && isSheetsForbiddenOrMissing) {
        setBootstrapError(
          '這份家庭帳本還沒分享給你的 Google 帳號。請對方在 Google Drive → 共用 → 加入你的 email（編輯者），然後再點一次邀請連結。',
        );
        // Clear the bad join id so a retry without re-clicking the link
        // doesn't loop on the same error.
        clearSharedSpreadsheetId();
      } else if (err instanceof SchemaTooNewError) {
        setBootstrapError(
          `你目前打開的是舊版 Mochia（支援 schema v${err.supportedVersion}），但你的帳本已經升級到 v${err.onDiskVersion}。請先把這個分頁完全關閉再重新打開，PWA 會自動拉到新版。`,
        );
      } else if (err instanceof InvalidTokenFormatError) {
        setBootstrapError(err.hint);
      } else if (hasHint(err)) {
        setBootstrapError(err.hint);
      } else {
        setBootstrapError(err instanceof Error ? err.message : String(err));
      }
      await handleLogout();
    } finally {
      setBootstrapping(false);
    }
    })();
    try {
      await _bootstrapInFlight;
    } finally {
      // Clear AFTER the work resolves so a retry (e.g. after logout +
      // re-login) starts a fresh pipeline. We deliberately keep the latch
      // alive while the pipeline is running so concurrent callers join
      // the same promise.
      _bootstrapInFlight = null;
    }
  }, [handleLogout]);

  const handleAuthenticated = useCallback(
    async (token: string, email: string | null) => {
      // Hand the token to both the in-memory session module (used by the
      // Sheets client to build the Authorization header) AND the auth slice
      // so React subscribers re-render.
      const expiresAt = Date.now() + 3600_000;
      setToken({ token, email, expiresAt });
      useStore.getState().auth.setSession({ token, email, expiresAt });
      // Persist the email separately so it can be displayed as a hint on
      // LoginScreen. Email is non-sensitive; the token never touches localStorage.
      if (email) setAccount(email);
      // Mark that a server-backed refresh-token cookie now exists so the
      // next cold load calls /api/auth/refresh instead of going straight to
      // LoginScreen (see design.md Open Questions #3 — Decision B).
      setCookieKnown();
      // Fetch Google profile (email + name + picture) — GIS' access-token
      // response carries no identity claims, so without this the
      // SettingsScreen user-card hero would show 「未登入」 forever. Fire-
      // and-forget; bootstrap proceeds in parallel and the avatar pops
      // in once the round-trip resolves.
      refreshUserProfile(token);
      await runBootstrapPipeline();
      if (readActiveId() !== null) navigate('/home');
    },
    [navigate, runBootstrapPipeline],
  );

  // Cold-load auth flow (priority order):
  //   1. dev-mode → fake token, run bootstrap directly.
  //   2. In-memory token present (unlikely after a PWA restart, but possible
  //      in a hot module reload or StrictMode re-mount) — skip cold load.
  //   3. hasCookieKnown() → try /api/auth/refresh to rehydrate session.
  //   4. No cookie flag → LoginScreen (no server round-trip for fresh users).
  useEffect(() => {
    if (readActiveId() !== null) return; // already bootstrapped this load
    if (isDevMode()) {
      void handleAuthenticated(DEV_FAKE_TOKEN, DEV_FAKE_EMAIL);
      return;
    }
    if (isAuthed) {
      // In-memory token still present — go straight to bootstrap.
      // Auth-slice profile fields (name/picture) aren't persisted, so
      // refetch userinfo on every cold mount with a still-valid token.
      const tok = getSessionToken();
      if (tok) refreshUserProfile(tok);
      void runBootstrapPipeline();
      return;
    }
    // No in-memory token — try server-backed refresh if we have a cookie.
    void runColdLoadAuth({
      hasCookieKnown,
      clearCookieKnown,
      getAccountHint: getAccount,
      clearAccountHint: clearAccount,
      postRefresh,
      setToken,
      getLastActive,
      setLastActive,
      clearLastActive,
      now: Date.now,
      onTokenRefreshed: () => {
        const tok = getSessionToken();
        const email = getAccount();
        if (tok) {
          useStore.getState().auth.setSession({
            token: tok,
            email,
            expiresAt: Date.now() + 3600_000,
          });
          refreshUserProfile(tok);
          void runBootstrapPipeline();
        }
      },
      onNoHint: () => {
        // hasCookieKnown() returned false → App renders LoginScreen. No-op.
      },
      onSilentRefreshFailed: ({ reason }) => {
        // eslint-disable-next-line no-console
        console.warn('[Mochia/auth] cold-load refresh failed', reason);
        setColdLoadFailureReason(reason);
      },
      onUpstreamError: () => {
        setUpstreamErrorToast(true);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAuthed) {
    // Handle the OAuth callback path before rendering LoginScreen.
    // The router is hash-based, so /auth/callback arrives as pathname not hash.
    if (typeof window !== 'undefined' && window.location.pathname === '/auth/callback') {
      return (
        <AuthCallbackScreen
          onAuthenticated={(token, email) => void handleAuthenticated(token, email)}
          onError={(reason) => {
            // Map callback error reasons to LoginScreen cold-load failure reasons
            // so the user gets actionable copy after returning from Google.
            const reasonMap: Record<string, SilentRefreshFailureReason> = {
              access_denied: 'grant_revoked',
              verifier_missing: 'unknown',
              exchange_failed: 'unknown',
            };
            setColdLoadFailureReason(reasonMap[reason] ?? 'unknown');
          }}
        />
      );
    }

    return (
      <>
        <LoginScreen
          coldLoadFailureReason={coldLoadFailureReason}
          migrationNotice={migrationNotice}
        />
        {bootstrapping && (
          <Toast open={true} message="連線到 Google Sheets…" onClose={() => {}} durationMs={0} />
        )}
        {bootstrapError && (
          <Toast
            open={true}
            message={`載入失敗：${bootstrapError}`}
            variant="error"
            onClose={() => setBootstrapError(null)}
          />
        )}
        {upstreamErrorToast && (
          <Toast
            open={true}
            message="暫時無法刷新登入，請稍後再試。"
            onClose={() => setUpstreamErrorToast(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <SchedulerEffect />
      <ShellRouter viewport={viewport} path={path} navigate={navigate} />
    </>
  );
}

/**
 * Mounts the recurring catch-up scheduler + visibility listener.
 * Extracted into its own component so the effect's lifecycle is tied to
 * the authed branch (unmounts cleanly on logout).
 */
function SchedulerEffect(): null {
  useEffect(() => {
    runScheduler(useStore.getState(), enqueueOp);

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        runScheduler(useStore.getState(), enqueueOp);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  return null;
}

/**
 * Initial load of the four data slices. Errors are surfaced by the caller.
 * Notifications + meta are also pulled (notifications populate the inbox;
 * `primary_currency` populates the metaCache slice).
 */
async function loadInitial(client: SheetsClient, spreadsheetId: string): Promise<void> {
  const ctx = { client, spreadsheetId };
  const [
    books,
    categories,
    transactions,
    recurring,
    notifications,
    primaryCurrencyRaw,
  ] = await Promise.all([
    booksRepo.list(client, spreadsheetId),
    categoriesRepo.list(client, spreadsheetId),
    transactionsRepo.list(client, spreadsheetId),
    recurringRepo.list(client, spreadsheetId),
    notificationsRepo.list(ctx),
    metaRepo.getValue(ctx, 'primary_currency').catch(() => undefined),
  ]);

  // Diagnostic — surfaces "data loaded" counts in the browser console so we
  // can tell whether bootstrap's seed actually flowed through to the store.
  // eslint-disable-next-line no-console
  console.info('[Mochia] data loaded', {
    books: books.length,
    categories: categories.length,
    transactions: transactions.length,
    recurring: recurring.length,
    notifications: notifications.length,
    primaryCurrencyRaw,
  });

  const s = useStore.getState();
  s.books.setItems(books);
  s.categories.setItems(categories);
  s.transactions.setItems(transactions);
  s.recurring.setItems(recurring);
  s.notifications.setItems(notifications);
  if (typeof primaryCurrencyRaw === 'string' && /^[A-Z]{3}$/.test(primaryCurrencyRaw.trim().toUpperCase())) {
    s.metaCache.setPrimaryCurrency(primaryCurrencyRaw.trim().toUpperCase());
  }
}

interface ShellRouterProps {
  viewport: ReturnType<typeof useViewport>;
  path: RoutePath;
  navigate: (to: RoutePath) => void;
}

function ShellRouter({ viewport, path, navigate }: ShellRouterProps): JSX.Element {
  const sidebarActive = useMemo<SidebarTabKey>(() => {
    if (path.startsWith('/settings')) return 'home';
    return ROUTE_TO_SIDEBAR[path] ?? 'home';
  }, [path]);

  // Mobile only has 帳本 / 分析 — every other route (settings, all-tx,
  // recur, search, notif, etc.) leaves both indicators inactive. Was
  // defaulting to 'home', but that broke "tap 帳本 from settings to go
  // home" — MobileShell's tap-same-tab handler scrolled-to-top instead
  // of navigating because it thought we were already on home.
  const mobileActive = useMemo<MobileTabKey | null>(() => {
    if (path === '/analytics') return 'analytics';
    if (path === '/home') return 'home';
    return null;
  }, [path]);

  // Books-sheet UI state — owned at the shell so it can be opened from
  // either the sidebar (desktop) or the home screen (mobile).
  const [booksOpen, setBooksOpen] = useState(false);
  const [newBookOpen, setNewBookOpen] = useState(false);
  const [editBook, setEditBook] = useState<BookRow | null>(null);
  const [addTxOpen, setAddTxOpen] = useState(false);
  const [editTx, setEditTx] = useState<TransactionRow | null>(null);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; variant?: 'info' | 'error' } | null>(null);

  const categories = useStore((s) => s.categories.items);
  const primaryCurrencyForAddTx = useStore((s) => s.metaCache.primaryCurrency);

  const activeBookId = useStore((s) => s.books.activeBookId);
  const books = useStore((s) => s.books.items).filter((b) => !b.deleted_at);
  const activeBook = useMemo<BookRow | null>(
    () => books.find((b) => b.id === activeBookId) ?? null,
    [books, activeBookId],
  );

  const screen = renderScreen(path, navigate, {
    onBookClick: () => setBooksOpen(true),
    // Design: avatar (`aria-label="設定"`) navigates to Settings, not a sheet.
    onProfile: () => navigate('/settings'),
    onEditTx: (tx) => setEditTx(tx),
    onDeleteTx: (tx) => onDeleteTx(tx),
    viewport,
  });

  const onActivateBook = useCallback(
    (id: string) => {
      useStore.getState().books.setActiveBookId(id);
      setBooksOpen(false);
    },
    [],
  );

  const onCreateBookSubmit = useCallback(
    (input: {
      name: string;
      color_token: string;
      type?: 'personal' | 'family' | 'project';
      seedCategoryIds?: string[];
    }) => {
      const id = repoGenId();
      const bookType = input.type ?? 'personal';
      const nowIso = new Date().toISOString();
      const row: BookRow = {
        id,
        name: input.name,
        color_token: input.color_token,
        type: bookType,
        is_default: false,
        rev: 1,
        created_at: nowIso,
        updated_at: nowIso,
        deleted_at: '',
      };
      const s = useStore.getState();
      s.books.addBook(row);
      s.books.setActiveBookId(id);
      enqueueOp({
        type: 'create_book',
        payload: {
          id,
          name: input.name,
          color_token: input.color_token,
          type: bookType,
          is_default: false,
        },
      });

      // v3 — seed cats fan-out. For each checked main, deep-copy the seed
      // (with a fresh id namespaced to this book) plus its subs into the
      // categories slice + sync queue. We never reuse the system seed ids
      // because those are reserved for the book_id='' shared seeds.
      const seedIds = new Set(input.seedCategoryIds ?? []);
      if (seedIds.size > 0) {
        const parentColor = new Map(
          SEED_CATEGORIES.map((c) => [c.id, c.color_token]),
        );
        let sortBase = s.categories.items.length;
        for (const main of SEED_CATEGORIES) {
          if (!seedIds.has(main.id)) continue;
          const newMainId = `${id}-${main.id}`;
          const mainRow: CategoryRow = {
            id: newMainId,
            book_id: id,
            parent_id: '',
            name: main.name,
            kind: main.kind,
            color_token: main.color_token,
            icon: main.icon,
            sort_order: sortBase++,
            // Per-book copies are NOT system seeds — the user can rename
            // / delete them without tripping SystemCategoryError.
            is_system: false,
            rev: 1,
            created_at: nowIso,
            updated_at: nowIso,
            deleted_at: '',
          };
          s.categories.addCategory(mainRow);
          enqueueOp({ type: 'create_category', payload: {
            id: mainRow.id,
            book_id: id,
            parent_id: '',
            name: mainRow.name,
            kind: mainRow.kind,
            color_token: mainRow.color_token,
            icon: mainRow.icon,
            sort_order: mainRow.sort_order,
            is_system: false,
          } });
          for (const sub of SEED_SUBCATEGORIES) {
            if (sub.parent_id !== main.id) continue;
            const subRow: CategoryRow = {
              id: `${id}-${sub.id}`,
              book_id: id,
              parent_id: newMainId,
              name: sub.name,
              kind: sub.kind,
              color_token: parentColor.get(main.id) ?? '--cat-other',
              icon: sub.icon,
              sort_order: sortBase++,
              is_system: false,
              rev: 1,
              created_at: nowIso,
              updated_at: nowIso,
              deleted_at: '',
            };
            s.categories.addCategory(subRow);
            enqueueOp({ type: 'create_category', payload: {
              id: subRow.id,
              book_id: id,
              parent_id: subRow.parent_id,
              name: subRow.name,
              kind: subRow.kind,
              color_token: subRow.color_token,
              icon: subRow.icon,
              sort_order: subRow.sort_order,
              is_system: false,
            } });
          }
        }
      }

      setNewBookOpen(false);
      setBooksOpen(false);
      setToast({ msg: `已新增「${input.name}」` });
    },
    [],
  );

  const renderBooksSheet = (): JSX.Element | null => {
    if (!booksOpen) return null;
    return (
      <BooksSheet
        books={books}
        activeBookId={activeBookId}
        onActivate={onActivateBook}
        onEdit={(book) => setEditBook(book)}
        onCreate={() => setNewBookOpen(true)}
        onClose={() => setBooksOpen(false)}
        variant={viewport === 'mobile' ? 'sheet' : 'modal'}
      />
    );
  };

  const onSaveBookEdit = useCallback(
    (book: BookRow, input: { name: string; color_token: string }) => {
      const s = useStore.getState();
      s.books.updateBook(book.id, {
        name: input.name,
        color_token: input.color_token,
        updated_at: new Date().toISOString(),
      });
      enqueueOp({
        type: 'update_book',
        payload: {
          id: book.id,
          rev: book.rev,
          patch: { name: input.name, color_token: input.color_token },
        },
      });
      setEditBook(null);
      setToast({ msg: `已更新「${input.name}」` });
    },
    [],
  );

  const onDeleteBook = useCallback(
    (book: BookRow) => {
      try {
      const s = useStore.getState();
        s.books.removeBook(book.id);
        enqueueOp({
        type: 'delete_book',
        payload: { id: book.id, rev: book.rev },
      });
        setEditBook(null);
        setToast({ msg: `已刪除「${book.name}」` });
      } catch (err) {
        if (err instanceof LastBookUndeletableError) {
          setToast({ msg: '至少要保留一本帳本', variant: 'error' });
          return;
        }
        throw err;
      }
    },
    [],
  );

  /** Internal: persist a tx (optimistic store + queue write) WITHOUT closing
   *  the screen. Used by both onSubmit (which then closes) and onContinue
   *  (which keeps the screen open for the next entry). */
  const persistTx = useCallback(
    (input: { book_id: string; amount_minor: number; currency: string; kind: 'expense' | 'income' | 'transfer'; category_id: string; subcategory_id: string; date: string; note: string; project: string; tags: string; recurring_id: string }) => {
      const id = repoGenId();
      const nowIso = new Date().toISOString();
      const row: TransactionRow = {
        id,
        ...input,
        rev: 1,
        created_at: nowIso,
        updated_at: nowIso,
        deleted_at: '',
      };
      const s = useStore.getState();
      s.transactions.upsert(row);
      enqueueOp({ type: 'create_tx', payload: row });
    },
    [],
  );

  const onAddTxSubmit = useCallback(
    (input: { book_id: string; amount_minor: number; currency: string; kind: 'expense' | 'income' | 'transfer'; category_id: string; subcategory_id: string; date: string; note: string; project: string; tags: string; recurring_id: string }) => {
      persistTx(input);
      setAddTxOpen(false);
      setEditTx(null);
      setToast({ msg: '已記入' });
    },
    [persistTx],
  );

  /** 繼續新增 — persists and stays on the AddTx screen. AddTxScreen
   *  internally resets the calculator + note fields after the callback
   *  returns, so the user lands ready for the next entry. */
  const onAddTxContinue = useCallback(
    (input: { book_id: string; amount_minor: number; currency: string; kind: 'expense' | 'income' | 'transfer'; category_id: string; subcategory_id: string; date: string; note: string; project: string; tags: string; recurring_id: string }) => {
      persistTx(input);
      setToast({ msg: '已記入，可以繼續輸入' });
    },
    [persistTx],
  );

  const onEditTxSubmit = useCallback(
    (target: TransactionRow, patch: { book_id: string; amount_minor: number; currency: string; kind: 'expense' | 'income' | 'transfer'; category_id: string; subcategory_id: string; date: string; note: string; project: string; tags: string }) => {
      const updated: TransactionRow = {
        ...target,
        ...patch,
        rev: target.rev + 1,
        updated_at: new Date().toISOString(),
      };
      const s = useStore.getState();
      s.transactions.upsert(updated);
      enqueueOp({
        type: 'update_tx',
        payload: { id: target.id, rev: target.rev, patch },
      });
      setEditTx(null);
      setAddTxOpen(false);
      setToast({ msg: '已更新' });
    },
    [],
  );

  const onDeleteTx = useCallback((target: TransactionRow) => {
      const s = useStore.getState();
    s.transactions.remove(target.id);
    enqueueOp({
        type: 'delete_tx',
        payload: { id: target.id, rev: target.rev },
      });
    setEditTx(null);
    setToast({ msg: '已刪除' });
  }, []);

  const renderAddTx = (): JSX.Element | null => {
    if (!addTxOpen && !editTx) return null;
    const target = editTx;
    const effectiveBookId = activeBookId ?? books[0]?.id ?? '';
    // v3 — only show this book's cats + system seeds. CategoryPicker will
    // narrow by `kind` itself.
    const visibleCats = categories.filter(
      (c) => c.book_id === '' || c.book_id === effectiveBookId,
    );
    // Tag suggestions = unique tags from all in-store transactions.
    const txItems = useStore.getState().transactions.items;
    const tagSet = new Set<string>();
    for (const t of txItems) {
      for (const tag of parseTags(t.tags)) tagSet.add(tag);
    }
    // Recent-notes index: per-subcategory top 6, global top 6, and the
    // full unique pool for the typing-mode search. OCR multi-line rows
    // are already filtered out by the helper.
    const recentNotesIndex = buildRecentNotesIndex(txItems);
    return (
      <AddTxScreen
        mode={target ? 'edit' : 'create'}
        books={books}
        activeBookId={effectiveBookId}
        categories={visibleCats}
        defaultCurrency={primaryCurrencyForAddTx}
        initial={target ?? undefined}
        tagSuggestions={Array.from(tagSet).sort()}
        recentNotesIndex={recentNotesIndex}
        onClose={() => {
          setAddTxOpen(false);
          setEditTx(null);
        }}
        onSubmit={(tx) => {
          if (target) onEditTxSubmit(target, tx);
          else onAddTxSubmit(tx);
        }}
        onContinue={target ? undefined : onAddTxContinue}
        onDelete={target ? () => onDeleteTx(target) : undefined}
        onScanReceipt={() => {
          setAddTxOpen(false);
          setOcrOpen(true);
        }}
      />
    );
  };

  const renderEditBookSheet = (): JSX.Element | null => {
    if (!editBook) return null;
    const visibleBooks = books.filter((b) => !b.deleted_at);
    return (
      <EditBookSheet
        book={editBook}
        deleteDisabled={visibleBooks.length <= 1}
        onClose={() => setEditBook(null)}
        onSave={(input) => onSaveBookEdit(editBook, input)}
        onDelete={() => onDeleteBook(editBook)}
        variant={viewport === 'mobile' ? 'sheet' : 'modal'}
      />
    );
  };

  const renderNewBookSheet = (): JSX.Element | null => {
    if (!newBookOpen) return null;
    try {
      // Defensive: if the only book got deleted somehow, block create-while-empty
      // edge cases via the store's invariant (it throws on remove).
      void LastBookUndeletableError;
    } catch {
      /* unreachable */
    }
    return (
      <NewBookSheet
        onClose={() => setNewBookOpen(false)}
        onCreate={onCreateBookSubmit}
        variant={viewport === 'mobile' ? 'sheet' : 'modal'}
      />
    );
  };

  if (viewport === 'mobile') {
    return (
      <>
        <MobileShell
          active={mobileActive}
          onHome={path === '/home'}
          onTab={(k) => navigate(mobileTabToRoute(k))}
          onAdd={() => setAddTxOpen(true)}
        >
          {screen}
        </MobileShell>
        {renderBooksSheet()}
        {renderNewBookSheet()}
        {renderEditBookSheet()}
        {renderAddTx()}
        {ocrOpen && <ReceiptScanScreen onClose={() => setOcrOpen(false)} />}
        {toast && (
          <Toast
            open={true}
            message={toast.msg}
            variant={toast.variant ?? 'info'}
            onClose={() => setToast(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <WebShell
        viewport={viewport}
        active={sidebarActive}
        onTab={(k) => navigate(sidebarTabToRoute(k))}
        onAdd={() => navigate('/all-tx')}
        onSettings={() => navigate('/settings')}
        onSearch={() => navigate('/search')}
        onNotif={() => navigate('/notif')}
        onBookClick={() => setBooksOpen(true)}
        bookName={activeBook?.name ?? '個人'}
        bookColor={activeBook?.color_token ?? '--terracotta-500'}
        title={titleFor(path)}
      >
        {screen}
      </WebShell>
      {renderBooksSheet()}
      {renderNewBookSheet()}
      {toast && (
        <Toast
          open={true}
          message={toast.msg}
          variant={toast.variant ?? 'info'}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}

function titleFor(path: RoutePath): string {
  if (path === '/settings') return '設定';
  if (path.startsWith('/settings/')) return '設定';
  if (path === '/login') return '登入';
  const sidebarKey = ROUTE_TO_SIDEBAR[path];
  return sidebarKey ? SIDEBAR_TITLES[sidebarKey] : 'Mochia';
}

function sidebarTabToRoute(k: SidebarTabKey): RoutePath {
  switch (k) {
    case 'home':
      return '/home';
    case 'all-tx':
      return '/all-tx';
    case 'analytics':
      return '/analytics';
    case 'cats':
      return '/cats';
    case 'recur':
      return '/recur';
    case 'search':
      return '/search';
    case 'notif':
      return '/notif';
  }
}

function mobileTabToRoute(k: MobileTabKey): RoutePath {
  switch (k) {
    case 'home':
      return '/home';
    case 'analytics':
      return '/analytics';
  }
}

interface RenderScreenContext {
  onBookClick: () => void;
  onProfile: () => void;
  onEditTx: (tx: TransactionRow) => void;
  onDeleteTx: (tx: TransactionRow) => void;
  viewport: ReturnType<typeof useViewport>;
}

function renderScreen(
  path: RoutePath,
  navigate: (to: RoutePath) => void,
  ctx: RenderScreenContext,
): JSX.Element {
  if (path === '/home') {
    return <HomeRoute ctx={ctx} navigate={navigate} />;
  }
  if (path === '/all-tx') {
    return <AllTxRoute navigate={navigate} onEditTx={ctx.onEditTx} />;
  }
  if (path === '/analytics') {
    return <AnalyticsRoute />;
  }
  if (path === '/recur') {
    // On mobile, surface a back chevron — the only path here from the
    // user's POV is settings → 固定週期, so the back arrow returning to
    // /settings is what they expect. Desktop relies on the sidebar.
    return (
      <RecurringScreen
        {...(ctx.viewport === 'mobile' ? { onBack: () => navigate('/settings') } : {})}
      />
    );
  }
  if (path === '/cats') {
    return <CategoriesRoute />;
  }
  if (path === '/search') {
    return <SearchRoute onEditTx={ctx.onEditTx} />;
  }
  if (path === '/notif') {
    return (
      <NotifCenterScreen
        onNavigate={(p) => {
          if (p === '#/recur') navigate('/recur');
          else if (p === '#/settings/sync') navigate('/settings/sync');
        }}
      />
    );
  }
  if (path === '/settings' || path.startsWith('/settings/')) {
    if (path === '/settings') {
      return <SettingsRoute onNavigate={navigate} />;
    }
    if (path === '/settings/notif') {
      return <NotifSettingsScreen onBack={() => navigate('/settings')} />;
    }
    if (path === '/settings/sync') {
      return <CloudSyncScreen onBack={() => navigate('/settings')} />;
    }
    if (path === '/settings/books') {
      return <SettingsBooksRoute ctx={ctx} navigate={navigate} />;
    }
    if (path === '/settings/cats') {
      return <CategoriesRoute onClose={() => navigate('/settings')} />;
    }
    if (path === '/settings/account') {
      return <SettingsRoute onNavigate={navigate} />;
    }
    return <PlaceholderScreen path={path} note="子設定畫面待後續任務實作" />;
  }
  return <PlaceholderScreen path={path} />;
}

function AllTxRoute({
  navigate: _navigate,
  onEditTx,
}: {
  navigate: (to: RoutePath) => void;
  onEditTx: (tx: TransactionRow) => void;
}): JSX.Element {
  const items = useStore((s) => s.transactions.items);
  const activeBookId = useStore((s) => s.books.activeBookId);
  const categories = useStore((s) => s.categories.items);
  const primaryCurrency = useStore((s) => s.metaCache.primaryCurrency);
  const visible = useMemo(
    () => items.filter((t) => !t.deleted_at && (!activeBookId || t.book_id === activeBookId)),
    [items, activeBookId],
  );
  return (
    <AllTxScreen
      transactions={visible}
      categories={categories}
      primaryCurrency={primaryCurrency}
      onTxClick={onEditTx}
    />
  );
}

function CategoriesRoute({ onClose }: { onClose?: () => void } = {}): JSX.Element {
  const categories = useStore((s) => s.categories.items);
  const activeBookId = useStore((s) => s.books.activeBookId);
  const transactions = useStore((s) => s.transactions.items);
  const recurring = useStore((s) => s.recurring.items);

  // v3 — filter by active book + system seeds.
  const visibleCategories = useMemo(
    () =>
      categories.filter(
        (c) =>
          c.book_id === '' || (activeBookId != null && c.book_id === activeBookId),
      ),
    [categories, activeBookId],
  );

  const refCheckFor = useCallback(
    (categoryId: string) => async () => {
      const txCount = transactions.filter(
        (t) => !t.deleted_at && (t.category_id === categoryId || t.subcategory_id === categoryId),
      ).length;
      const recurringCount = recurring.filter(
        (r) =>
          !r.deleted_at && (r.category_id === categoryId || r.subcategory_id === categoryId),
      ).length;
      return { txCount, recurringCount };
    },
    [transactions, recurring],
  );

  const onCreate = useCallback(
    async (input: { name: string; kind: 'expense' | 'income'; color_token: string; icon: string; parent_id?: string }) => {
      const id = repoGenId();
      const next = categories.length;
      // New user-created categories live in the active book. Falling back
      // to '' (system-seed namespace) would let an unlogged-in flow leak
      // a personal cat into shared seeds — defensively coerce here.
      const bookId = activeBookId ?? '';
      const row: CategoryRow = {
        id,
        book_id: bookId,
        parent_id: input.parent_id ?? '',
        name: input.name,
        kind: input.kind,
        color_token: input.color_token,
        icon: input.icon,
        sort_order: next,
        is_system: false,
        rev: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: '',
      };
      const s = useStore.getState();
      s.categories.addCategory(row);
      enqueueOp({
        type: 'create_category',
        payload: {
          id: row.id,
          book_id: row.book_id,
          parent_id: row.parent_id,
          name: row.name,
          kind: row.kind,
          color_token: row.color_token,
          icon: row.icon,
          sort_order: row.sort_order,
          is_system: row.is_system,
        },
      });
    },
    [categories, activeBookId],
  );

  const onUpdate = useCallback(
    async (id: string, input: { name: string; kind: 'expense' | 'income'; color_token: string; icon: string; parent_id?: string }) => {
      const target = categories.find((c) => c.id === id);
      if (!target) return;
      const s = useStore.getState();
      s.categories.updateCategory(id, { ...input, updated_at: new Date().toISOString() });
      enqueueOp({
        type: 'update_category',
        payload: { id, rev: target.rev, patch: input },
      });
    },
    [categories],
  );

  const onDelete = useCallback(
    async (id: string) => {
      const target = categories.find((c) => c.id === id);
      if (!target) return;
      const s = useStore.getState();
      await s.categories.deleteCategory(id, refCheckFor(id));
      enqueueOp({
        type: 'delete_category',
        payload: { id, rev: target.rev },
      });
    },
    [categories, refCheckFor],
  );

  const onReorder = useCallback(
    (_parentId: string, changes: Array<{ id: string; sort_order: number }>) => {
      // Optimistic Local Reorder With OCC Conflict Path. Implementation
      // lives in `applyReorder` (pure, testable) — we just thread the
      // store + sync-queue dependencies in. OCC conflicts surface via
      // the existing `occ_conflict` notification path in
      // `sync-queue.ts`; we do not roll back local state on a single
      // failed op (per spec).
      applyReorder(changes, {
        getCategories: () => useStore.getState().categories.items,
        applyLocal: (id, patch) =>
          useStore.getState().categories.updateCategory(id, patch),
        enqueue: enqueueOp,
      });
    },
    [],
  );

  return (
    <CategoriesScreen
      categories={visibleCategories}
      refCheckFor={refCheckFor}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onDelete={onDelete}
      onReorder={onReorder}
      {...(onClose ? { onClose } : {})}
    />
  );
}

function SearchRoute({
  onEditTx,
}: {
  onEditTx: (tx: TransactionRow) => void;
}): JSX.Element {
  const items = useStore((s) => s.transactions.items);
  const categories = useStore((s) => s.categories.items);
  const books = useStore((s) => s.books.items).filter((b) => !b.deleted_at);
  const primaryCurrency = useStore((s) => s.metaCache.primaryCurrency);
  const visible = useMemo(() => items.filter((t) => !t.deleted_at), [items]);
  return (
    <SearchScreen
      transactions={visible}
      categories={categories}
      books={books}
      primaryCurrency={primaryCurrency}
      onTxClick={onEditTx}
    />
  );
}

function SettingsBooksRoute({
  ctx,
  navigate,
}: {
  ctx: RenderScreenContext;
  navigate: (to: RoutePath) => void;
}): JSX.Element {
  // Settings → 帳本管理 just opens the BooksSheet on top of the Settings
  // page (it's a contextual sheet, not a route in its own right). When the
  // user closes the sheet we send them back to /settings so refreshing the
  // route doesn't trap them on a blank page.
  useEffect(() => {
    ctx.onBookClick();
    navigate('/settings');
  }, [ctx, navigate]);
  // Render the Settings page underneath rather than a placeholder so the
  // backdrop never shows debug-looking copy like "/settings/books ...".
  return <SettingsScreen onNavigate={navigate} />;
}

/**
 * SettingsRoute — wires the SettingsScreen with live store-derived counts
 * for the rows that show details (帳本管理 "N 本", 分類管理 "N 類",
 * 固定週期 "N 項"). primary_currency is consumed from the metaCache slice.
 */
function SettingsRoute({
  onNavigate,
}: {
  onNavigate: (to: RoutePath) => void;
}): JSX.Element {
  const books = useStore((s) => s.books.items).filter((b) => !b.deleted_at);
  const categories = useStore((s) => s.categories.items);
  const recurring = useStore((s) => s.recurring.items);

  const bookCount = books.filter((b) => !b.deleted_at).length;
  // Count main categories only (parent_id empty) — sub-categories are
  // shown in 分類管理 itself but the headline count is mains.
  const catCount = categories.filter(
    (c) => !c.deleted_at && !c.parent_id,
  ).length;
  const recurCount = recurring.filter(
    (r) => !r.deleted_at && r.active,
  ).length;

  const details = {
    books: `${bookCount} 本`,
    cats: `${catCount} 類`,
    recur: `${recurCount} 項`,
    sync: '已啟用',
  };

  const primaryCurrency = useStore((s) => s.metaCache.primaryCurrency);
  const setPrimaryCurrencyLocal = useStore((s) => s.metaCache.setPrimaryCurrency);

  const onPrimaryCurrencyChange = useCallback(
    (code: string) => {
      const normalized = code.trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(normalized)) return;
      setPrimaryCurrencyLocal(normalized);
      enqueueOp({
        type: 'set_meta',
        payload: { key: 'primary_currency', value: normalized },
      });
    },
    [setPrimaryCurrencyLocal],
  );

  return (
    <SettingsScreen
      onNavigate={onNavigate}
      details={details}
      primaryCurrency={primaryCurrency}
      onPrimaryCurrencyChange={onPrimaryCurrencyChange}
    />
  );
}

function AnalyticsRoute(): JSX.Element {
  const currency = useStore((s) => s.metaCache.primaryCurrency);
  return <AnalyticsScreen currency={currency} />;
}

function HomeRoute({
  ctx,
  navigate,
}: {
  ctx: RenderScreenContext;
  navigate: (to: RoutePath) => void;
}): JSX.Element {
  const books = useStore((s) => s.books.items).filter((b) => !b.deleted_at);
  const activeBookId = useStore((s) => s.books.activeBookId);
  const transactions = useStore((s) => s.transactions.items);
  const categories = useStore((s) => s.categories.items);
  const unreadCount = useStore((s) => s.notifications.unreadCount);
  const primaryCurrency = useStore((s) => s.metaCache.primaryCurrency);
  const activeBook = useMemo<BookRow | null>(
    () => books.find((b) => b.id === activeBookId) ?? null,
    [books, activeBookId],
  );
  return (
    <HomeScreen
      activeBook={activeBook}
      books={books}
      transactions={transactions}
      categories={categories}
      today={new Date()}
      primaryCurrency={primaryCurrency}
      onSwitchBook={ctx.onBookClick}
      onSearch={() => navigate('/search')}
      onNotif={() => navigate('/notif')}
      onProfile={ctx.onProfile}
      onTxClick={ctx.onEditTx}
      onTxDelete={ctx.onDeleteTx}
      hasUnreadNotif={unreadCount > 0}
      desktopLayout={ctx.viewport === 'wide' || ctx.viewport === 'tablet'}
      onViewAll={() => navigate('/all-tx')}
      rightRail={
        ctx.viewport === 'wide' ? (
          <HomeRightRail
            transactions={transactions}
            categories={categories}
            today={new Date()}
            currency={primaryCurrency}
            onOpenAnalytics={() => navigate('/analytics')}
          />
        ) : null
      }
    />
  );
}

function HomeRightRail({
  transactions,
  categories,
  today,
  currency,
  onOpenAnalytics,
}: {
  transactions: TransactionRow[];
  categories: CategoryRow[];
  today: Date;
  currency: string;
  onOpenAnalytics: () => void;
}): JSX.Element {
  const result = useMemo(
    () =>
      buildPercento(transactions, categories, {
        mode: 'expense',
        period: {
          start: formatYmd(startOfMonth(today)),
          end: formatYmd(endOfMonth(today)),
        },
        currency,
      }),
    [transactions, categories, today, currency],
  );
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            font: '500 11px var(--font-cjk)',
            color: 'var(--fg-3)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          本月分類
        </div>
        <button
          type="button"
          onClick={onOpenAnalytics}
          aria-label="開啟分析"
          style={{
            font: '500 12px var(--font-cjk)',
            color: 'var(--accent)',
            background: 'none',
            border: 0,
            padding: 0,
            cursor: 'pointer',
          }}
        >
          看更多 →
        </button>
      </div>
      {result.total_minor === 0 ? (
        <div
          style={{
            font: '400 13px/1.6 var(--font-cjk)',
            color: 'var(--fg-3)',
            padding: '12px 0',
          }}
        >
          本月還沒有支出紀錄。
        </div>
      ) : (
        <PercentoBar result={result} />
      )}
    </div>
  );
}

function PlaceholderScreen({
  path,
  note,
}: {
  path: RoutePath;
  note?: string;
}): JSX.Element {
  return (
    <div
      style={{
        padding: '40px 24px',
        font: '500 14px var(--font-cjk)',
        color: 'var(--fg-3)',
      }}
    >
      <div style={{ font: '600 18px var(--font-cjk-display)', color: 'var(--fg-1)', marginBottom: 6 }}>
        {path}
      </div>
      <div>{note ?? '本畫面將在後續任務中實作。'}</div>
    </div>
  );
}
